/**
 * Key scope — which properties may rely on GTM's cumulative data model.
 *
 * Every `dataLayer.push` merges into one model, and a key keeps its value until
 * something overwrites it. Omitting a key therefore does not clear it: it leaves
 * the previous event's value applying to this one. A key may only rely on that
 * inheritance when its scope genuinely outlives the event that set it.
 *
 * - **Sticky** — page context, experiment assignment, and who is acting. One page
 *   view sets these and they stay true for every event that follows, which is
 *   what keeps call sites from having to repeat the brand, locale or user on
 *   every click.
 * - **Everything else** — what just happened: the element clicked, the step
 *   submitted, the values entered, the errors raised. There is no correct value
 *   to inherit here, so the previous event's is simply wrong.
 *
 * The default is to clear, so a field an app adds is safe without anyone thinking
 * about it, and stickiness is an explicit opt-in via `addStickyKeys`.
 */

/**
 * Keys whose scope outlives the event that set them. Wire names (snake_case),
 * because clearing happens after the case transform, on the built event.
 */
export const STICKY_KEYS: readonly string[] = [
    // Page context — set by the page view, true for everything on that page.
    'audience_type',
    'brand_id',
    'brand_name',
    'business_category',
    'business_category_id',
    'business_category_sub',
    'business_category_sub_id',
    'business_unit',
    'locale',
    'page_location',
    'page_template',
    'page_type',

    // Experiment assignment — page/session scoped, never per-event.
    'ab_test_variant',

    // Who is acting. Identity, not measurement.
    'company',
    'eid_sha256',
    'pid_sha256',
    'user_id',
    'user_type',

    // Which lead the visitor is working on. Every event on the page belongs to
    // the same one, and `lead` is replaced wholesale rather than merged, so a
    // richer form_success lead never leaves stale attributes behind.
    'lead',
];

/**
 * `event` names the message itself and is set by every push, so it can never go
 * stale and must never be cleared.
 */
const EVENT_NAME_KEY = 'event';

interface KeyScopeRegistry {
    sticky: Set<string>;
    /** Non-sticky keys the previous push put into the model. */
    pushed: Set<string>;
}

type KeyScopeRegistryHost = typeof globalThis & {
    __skydreamsTrackingKeyScope?: KeyScopeRegistry;
};

/**
 * Held on globalThis for the same reason the ab-variant provider is: a page can
 * run several copies of this package, and they all push into one shared
 * dataLayer. A per-module registry would only clear the keys its own copy
 * pushed, which is exactly the stale-value bug it exists to prevent.
 */
function getKeyScopeRegistry(): KeyScopeRegistry {
    const host = globalThis as KeyScopeRegistryHost;

    if (!host.__skydreamsTrackingKeyScope) {
        host.__skydreamsTrackingKeyScope = {
            sticky: new Set(STICKY_KEYS),
            pushed: new Set(),
        };
    }

    return host.__skydreamsTrackingKeyScope;
}

/**
 * Marks app-specific keys as page-scoped, so they are inherited rather than
 * cleared. Wire names (snake_case). Use it only for values that stay true for
 * every event on the page — an app-level dimension, not something describing a
 * single interaction.
 */
export function addStickyKeys(keys: readonly string[]): void {
    const registry = getKeyScopeRegistry();

    keys.forEach((key) => {
        registry.sticky.add(key);
        registry.pushed.delete(key);
    });
}

function isTrackingEvent(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object'
        && value !== null
        && !Array.isArray(value)
        && typeof (value as Record<string, unknown>)[EVENT_NAME_KEY] === 'string';
}

/**
 * Returns the payload to push: the event plus an explicit `undefined` for every
 * non-sticky key an earlier event left in the model that this one does not set.
 *
 * Anything that is not a tracking event (GTM control messages, consent state) is
 * passed through untouched and does not update the registry.
 */
export function withStaleKeysCleared(event: unknown): unknown {
    if (!isTrackingEvent(event)) {
        return event;
    }

    const registry = getKeyScopeRegistry();
    const ownKeys = Object.keys(event);
    const own = new Set(ownKeys);
    const cleared: Record<string, undefined> = {};

    registry.pushed.forEach((key) => {
        if (!own.has(key)) {
            cleared[key] = undefined;
        }
    });

    registry.pushed = new Set(
        ownKeys.filter((key) => key !== EVENT_NAME_KEY && !registry.sticky.has(key))
    );

    return Object.keys(cleared).length > 0 ? { ...cleared, ...event } : event;
}
